import React, { useEffect, useState } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Alert } from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL;
const EMP_API = `${API_URL}/api/employees`;
const LEAVE_API = `${API_URL}/api/leaves`;

export default function LeaveApplication({ user }) {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState('');
  const [leaves, setLeaves] = useState([]);
  const [form, setForm] = useState({ from_date: '', to_date: '', reason: '' });
  const [error, setError] = useState('');
  const [canApply, setCanApply] = useState(true);
  const isAdmin = user && user.role === 'admin';
  const isEmployee = user && user.role === 'employee';

  useEffect(() => {
    if (isAdmin) {
      fetch(EMP_API).then(r => r.json()).then(setEmployees);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      if (selected) {
        fetch(`${LEAVE_API}/${selected}`).then(r => r.json()).then(setLeaves);
      } else {
        setLeaves([]);
      }
    } else if (isEmployee) {
      // For employee, auto-select their own employee_id
      if (user.employee_id) {
        setSelected(user.employee_id);
        fetch(`${LEAVE_API}/${user.employee_id}`).then(r => r.json()).then(setLeaves);
      }
    }
  }, [selected, isAdmin, isEmployee, user]);

  useEffect(() => {
    if (!form.from_date || !form.to_date) {
      setError('');
      setCanApply(false);
      return;
    }
    const now = new Date();
    const from = new Date(form.from_date);
    const to = new Date(form.to_date);
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const nextMonth = (currentMonth + 1) % 12;
    const fromMonth = from.getMonth();
    const toMonth = to.getMonth();
    const fromYear = from.getFullYear();
    const toYear = to.getFullYear();
    // Only allow current year
    if (fromYear !== currentYear || toYear !== currentYear) {
      setError('Only current year is allowed.');
      setCanApply(false);
      return;
    }
    // Only allow current or next month
    const validMonth = (
      (fromMonth === currentMonth || fromMonth === nextMonth) &&
      (toMonth === currentMonth || toMonth === nextMonth)
    );
    // Disallow previous months
    if (from < new Date(currentYear, currentMonth, 1)) {
      setError('Cannot apply for previous months.');
      setCanApply(false);
      return;
    }
    // Only allow up to 1 month in advance
    if (!validMonth) {
      setError('Can only apply for current or next month.');
      setCanApply(false);
      return;
    }
    // Valid range
    setError('');
    setCanApply(true);
  }, [form.from_date, form.to_date]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected || !form.from_date || !form.to_date || !form.reason) return;
    await fetch(LEAVE_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ employee_id: selected, ...form }),
    });
    setForm({ from_date: '', to_date: '', reason: '' });
    fetch(`${LEAVE_API}/${selected}`).then(r => r.json()).then(setLeaves);
  };

  const handleApprove = async (leaveId) => {
    await fetch(`${LEAVE_API}/${leaveId}/approve`, { method: 'POST' });
    fetch(`${LEAVE_API}/${selected}`).then(r => r.json()).then(setLeaves);
  };

  // Helper: restrict date pickers
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const minDate = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`;
  const maxDate = `${currentYear}-12-31`;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Leave Application</Typography>
      {!isEmployee && (
        <FormControl sx={{ minWidth: 200, mb: 3 }}>
          <InputLabel>Employee</InputLabel>
          <Select value={selected} label="Employee" onChange={e => setSelected(e.target.value)}>
            <MenuItem value=""><em>None</em></MenuItem>
            {employees.map(emp => (
              <MenuItem key={emp.employee_id} value={emp.employee_id}>{emp.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <TextField label="From" name="from_date" type="date" value={form.from_date} onChange={handleChange} InputLabelProps={{ shrink: true }} required inputProps={{ min: minDate, max: maxDate }} />
        <TextField label="To" name="to_date" type="date" value={form.to_date} onChange={handleChange} InputLabelProps={{ shrink: true }} required inputProps={{ min: minDate, max: maxDate }} />
        <TextField label="Reason" name="reason" value={form.reason} onChange={handleChange} required />
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Button type="submit" variant="contained" disabled={!!error || !canApply}>Apply</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Status</TableCell>
              {isAdmin && <TableCell>Action</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {leaves.length === 0 ? (
              <TableRow><TableCell colSpan={isAdmin ? 5 : 4}>No leave records found.</TableCell></TableRow>
            ) : (
              leaves.map((l, i) => (
                <TableRow key={i}>
                  <TableCell>{l.from_date}</TableCell>
                  <TableCell>{l.to_date}</TableCell>
                  <TableCell>{l.reason}</TableCell>
                  <TableCell>{l.status}</TableCell>
                  {isAdmin && (
                    <TableCell>
                      {l.status === 'Pending' && (
                        <Button variant="contained" color="success" size="small" onClick={() => handleApprove(l.id)}>
                          Approve
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
} 